import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getTemplates, addTemplate, deleteTemplate } from '../lib/supabase';
import { NAVY, GOLD, GOLD_LIGHT, WHITE, BG } from '../lib/ui';
import { Field, Select, Input, Btn, Empty, Card } from '../lib/ui';

const ADMIN_EMAIL = 'emrekoluman@koluman.av.tr';

const CATEGORIES = [
  'KYC & Client Onboarding',
  'Engagement Letters',
  'NDA',
  'Service Agreements',
  'Corporate Documents',
  'Court & Litigation',
  'Regulatory Filings',
  'Internal Policies',
  'General',
];

const CATEGORY_ICONS = {
  'KYC & Client Onboarding':  '🪪',
  'Engagement Letters':        '✉️',
  'NDA':                       '🔒',
  'Service Agreements':        '📋',
  'Corporate Documents':       '🏢',
  'Court & Litigation':        '⚖️',
  'Regulatory Filings':        '📜',
  'Internal Policies':         '📌',
  'General':                   '📄',
};

const fmt     = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtSize = b => b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;

const fileIcon = name => {
  const ext = name.split('.').pop().toLowerCase();
  if (ext === 'pdf')                      return '📄';
  if (['doc','docx'].includes(ext))       return '📝';
  if (['xls','xlsx'].includes(ext))       return '📊';
  if (['ppt','pptx'].includes(ext))       return '📑';
  if (['jpg','jpeg','png'].includes(ext)) return '🖼';
  return '📎';
};

export default function TemplateLibrary({ currentUserEmail }) {
  const isAdmin = currentUserEmail === ADMIN_EMAIL;

  const [templates,   setTemplates]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [activeCategory, setCategory] = useState('All');
  const [uploading,   setUploading]   = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [showForm,    setShowForm]    = useState(false);
  const [form,        setForm]        = useState({ category:'KYC & Client Onboarding', description:'' });
  const [pendingFile, setPendingFile] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    getTemplates().then(data => { setTemplates(data || []); setLoading(false); });
  }, []);

  const handleFileSelect = (file) => {
    if (!file) return;
    setPendingFile(file);
    setShowForm(true);
  };

  const handleUpload = async () => {
    if (!pendingFile) return;
    setUploading(true); setUploadError('');
    try {
      const safeName = pendingFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `templates/${form.category.replace(/[^a-zA-Z0-9]/g,'_')}/${Date.now()}_${safeName}`;
      const { error: upErr } = await supabase.storage.from('documents').upload(path, pendingFile);
      if (upErr) throw upErr;
      const tmpl = await addTemplate({
        file_name:    pendingFile.name,
        file_size:    pendingFile.size,
        storage_path: path,
        category:     form.category,
        description:  form.description || null,
        uploaded_by:  currentUserEmail,
      });
      setTemplates(ts => [...ts, tmpl].sort((a,b) => a.category.localeCompare(b.category) || a.file_name.localeCompare(b.file_name)));
      setShowForm(false); setPendingFile(null); setForm({ category:'KYC & Client Onboarding', description:'' });
    } catch (e) {
      setUploadError(e.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (tmpl) => {
    const { data, error } = await supabase.storage.from('documents').createSignedUrl(tmpl.storage_path, 300);
    if (error) { alert('Could not get download link.'); return; }
    window.location.href = data.signedUrl;
  };

  const handleDelete = async (tmpl) => {
    if (!window.confirm(`Delete "${tmpl.file_name}"? This cannot be undone.`)) return;
    await supabase.storage.from('documents').remove([tmpl.storage_path]);
    await deleteTemplate(tmpl.id);
    setTemplates(ts => ts.filter(t => t.id !== tmpl.id));
  };

  // Filter
  const filtered = templates.filter(t =>
    (activeCategory === 'All' || t.category === activeCategory) &&
    (!search || t.file_name.toLowerCase().includes(search.toLowerCase()) || (t.description||'').toLowerCase().includes(search.toLowerCase()))
  );

  // Group by category
  const grouped = filtered.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  const allCategories = [...new Set(templates.map(t => t.category))].sort();

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* Header */}
      <div style={{ background:WHITE, borderRadius:10, border:'1px solid #E5E0D8', borderTop:`3px solid ${GOLD}`, padding:'18px 22px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <div style={{ fontSize:32 }}>📚</div>
          <div>
            <div style={{ fontFamily:'Georgia,serif', fontSize:20, fontWeight:700, color:NAVY }}>Template Library</div>
            <div style={{ fontSize:12, color:'#888', marginTop:2 }}>{templates.length} template{templates.length!==1?'s':''} · shared with all team members</div>
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <input ref={fileRef} type="file" style={{ display:'none' }} onChange={e => handleFileSelect(e.target.files[0])} />
          <button onClick={() => fileRef.current?.click()} style={{ background:NAVY, color:GOLD, border:'none', borderRadius:7, padding:'9px 18px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
            + Upload Template
          </button>
        </div>
      </div>

      {/* Upload form */}
      {showForm && pendingFile && (
        <div style={{ background:WHITE, borderRadius:10, border:`1px solid ${GOLD}55`, borderLeft:`4px solid ${GOLD}`, padding:'18px 22px' }}>
          <div style={{ fontSize:14, fontWeight:700, color:NAVY, marginBottom:14 }}>
            {fileIcon(pendingFile.name)} {pendingFile.name} <span style={{ fontSize:12, color:'#AAA', fontWeight:400 }}>({fmtSize(pendingFile.size)})</span>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <Field label="Category">
              <Select value={form.category} onChange={e => setForm({...form, category:e.target.value})}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Description (optional)">
              <Input value={form.description} onChange={e => setForm({...form, description:e.target.value})} placeholder="e.g. Standard KYC form for UAE clients" />
            </Field>
          </div>
          {uploadError && <div style={{ marginTop:10, fontSize:12, color:'#C0392B' }}>⚠ {uploadError}</div>}
          <div style={{ display:'flex', gap:8, marginTop:14 }}>
            <Btn onClick={handleUpload} style={{ opacity: uploading ? 0.6 : 1 }}>{uploading ? 'Uploading…' : 'Upload to Library'}</Btn>
            <Btn variant="ghost" onClick={() => { setShowForm(false); setPendingFile(null); }}>Cancel</Btn>
          </div>
        </div>
      )}

      {/* Search + category filter */}
      <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
        <Input placeholder="Search templates…" value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth:280 }} />
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          {['All', ...allCategories].map(cat => (
            <button key={cat} onClick={() => setCategory(cat)} style={{
              background: activeCategory===cat ? NAVY : WHITE,
              color: activeCategory===cat ? GOLD : '#666',
              border: `1px solid ${activeCategory===cat ? NAVY : '#D0CBBF'}`,
              borderRadius:20, padding:'5px 14px', fontSize:12, fontWeight: activeCategory===cat ? 700 : 400,
              cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap',
            }}>
              {cat === 'All' ? 'All' : `${CATEGORY_ICONS[cat]||'📄'} ${cat}`}
            </button>
          ))}
        </div>
      </div>

      {/* Template groups */}
      {loading && <div style={{ color:'#BBB', textAlign:'center', padding:32 }}>Loading templates…</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ background:WHITE, borderRadius:10, border:'1px solid #E5E0D8', padding:'48px 32px', textAlign:'center' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📭</div>
          <div style={{ fontSize:15, fontWeight:600, color:NAVY, marginBottom:6 }}>
            {search ? 'No templates match your search' : 'No templates yet'}
          </div>
          <div style={{ fontSize:13, color:'#AAA' }}>
            {search ? 'Try a different search term' : 'Click "+ Upload Template" to add your first template'}
          </div>
        </div>
      )}

      {Object.entries(grouped).map(([category, items]) => (
        <div key={category}>
          {/* Category header */}
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
            <span style={{ fontSize:20 }}>{CATEGORY_ICONS[category]||'📄'}</span>
            <span style={{ fontFamily:'Georgia,serif', fontSize:15, fontWeight:700, color:NAVY }}>{category}</span>
            <span style={{ fontSize:12, color:'#AAA' }}>({items.length})</span>
            <div style={{ flex:1, height:1, background:'#F0EDE8' }} />
          </div>

          {/* Template rows */}
          <div style={{ background:WHITE, borderRadius:10, border:'1px solid #E5E0D8', overflow:'hidden' }}>
            {items.map((tmpl, i) => (
              <div key={tmpl.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 18px', borderBottom: i < items.length-1 ? '1px solid #F7F5F0' : 'none' }}>
                <div style={{ fontSize:24, flexShrink:0 }}>{fileIcon(tmpl.file_name)}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:NAVY, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {tmpl.file_name}
                  </div>
                  <div style={{ fontSize:11, color:'#AAA', marginTop:2 }}>
                    {fmtSize(tmpl.file_size)}
                    {tmpl.description && <span> · {tmpl.description}</span>}
                    <span> · Uploaded by {tmpl.uploaded_by} · {fmt(tmpl.created_at?.slice(0,10))}</span>
                  </div>
                </div>
                <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                  <button onClick={() => handleDownload(tmpl)} style={{ background:NAVY, color:GOLD, border:'none', borderRadius:7, padding:'8px 16px', cursor:'pointer', fontSize:12, fontWeight:700, fontFamily:'inherit', minHeight:40 }}>
                    ↓ Download
                  </button>
                  {isAdmin && (
                    <button onClick={() => handleDelete(tmpl)} style={{ background:'none', border:'1px solid #F5C0B8', borderRadius:7, padding:'8px 12px', cursor:'pointer', fontSize:13, color:'#C0392B', minHeight:40 }}>
                      ×
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
